/**
 * AudioService - A service for handling text-to-speech functionality
 * using the Web Speech API (initially Chrome-only support)
 */
import { logger } from './utils';

// Global array to store utterance references to prevent garbage collection in Chrome
// This fix comes from: https://stackoverflow.com/questions/23483990/speechsynthesis-api-onend-callback-not-working
window.utterances = window.utterances || [];

class AudioService {
  static instance = null;
  
  constructor() {
    // If an instance already exists, return it (singleton pattern)
    if (AudioService.instance) {
      return AudioService.instance;
    }
    
    this.synth = window.speechSynthesis;
    
    // DEBUG: Log if speechSynthesis is available only in dev mode
    if (process.env.NODE_ENV === 'development') {
      logger.audio(`Speech synthesis available: ${!!this.synth}`);
    }
    
    this.utterance = null;
    this.isPlaying = false;
    this.currentText = '';
    this.voices = [];
    this.isInitialized = false;
    this.intentionalStop = false;
    
    // For pause/resume functionality
    this.isPaused = false;
    this.shouldRememberPosition = false;
    
    // For resuming text at the previous position
    this.lastText = '';
    this.lastPosition = 0;
    this.lastOptions = {};
    this.currentChunkIndex = 0;
    this._pausedAtChunkIndex = 0; // Special tracking for exact pause position
    
    // Store the instance
    AudioService.instance = this;
  }

  initialize() {
    // Skip initialization if already completed
    if (this.isInitialized && this.voices.length > 0) {
      logger.debug('AudioService', `Already initialized with ${this.voices.length} voices`);
      return Promise.resolve(this.voices);
    }
    
    // Check if speech synthesis is accessible
    if (!this.synth) {
      logger.error('AudioService', 'Speech synthesis not available');
      return Promise.resolve([]);
    }
    
    // DEBUG: Log speech synthesis state only in dev mode
    if (process.env.NODE_ENV === 'development') {
      logger.debug('AudioService', `Speech synthesis state: speaking=${this.synth.speaking}, paused=${this.synth.paused}`);
    }
    
    // Load available voices (may be async in Chrome)
    this.voices = this.synth.getVoices();
    
    if (process.env.NODE_ENV === 'development') {
      logger.debug('AudioService', `Initial voice count: ${this.voices.length}`);
    }
    
    // Always add the listener if voices are not available initially
    // This works for Chrome and is harmless for other browsers
    if (this.voices.length === 0) {
      logger.debug('AudioService', 'No voices available yet, adding listener');
      this.synth.addEventListener('voiceschanged', () => {
        this.voices = this.synth.getVoices();
        logger.debug('AudioService', `Voices loaded, found: ${this.voices.length}`);
      });
    }
    
    // Return a promise that resolves when voices are available
    return new Promise((resolve) => {
      // Check if voices are already available
      if (this.voices.length > 0) {
        this.isInitialized = true;
        resolve(this.voices);
        return;
      }
      
      // Set up a listener to resolve the promise when voices are loaded
      const voicesChangedHandler = () => {
        this.voices = this.synth.getVoices();
        logger.audio(`Voices loaded: ${this.voices.length}`);
        this.synth.removeEventListener('voiceschanged', voicesChangedHandler);
        this.isInitialized = true;
        resolve(this.voices);
      };
      
      // Add the event listener (for Chrome)
      this.synth.addEventListener('voiceschanged', voicesChangedHandler);
      
      // Also set up a fallback for non-Chrome browsers that might not trigger the event
      let attempts = 0;
      const maxAttempts = 20;
      const checkVoices = () => {
        // If the promise is already resolved by the event handler, this won't do anything
        if (this.voices.length > 0) {
        logger.audio(`Voices loaded through polling: ${this.voices.length}`);
          this.isInitialized = true;
          resolve(this.voices);
          return;
        }
        
        // Try again a few times
        if (attempts < maxAttempts) {
          attempts++;
          this.voices = this.synth.getVoices();
          const delay = Math.min(100 * attempts, 1000); // Exponential backoff up to 1 second
          setTimeout(checkVoices, delay);
        } else {
          logger.audio(`Failed to load voices after ${maxAttempts} attempts`, true);
          resolve([]); // Resolve with empty array
        }
      };
      
      // Start the polling after a short delay
      setTimeout(checkVoices, 300);
    });
  }

  speak(text, options = {}, retryCount = 0, startPosition = 0) {
    if (!text || typeof text !== 'string') {
      logger.error('AudioService', 'Invalid text provided to speak');
      return false;
    }
    
    // Limit retry attempts
    const MAX_RETRIES = 3;
    if (retryCount > MAX_RETRIES) {
      logger.error('AudioService', `Speech synthesis failed after ${MAX_RETRIES} retries`);
      return false;
    }
    
    // Remember original text and options for possible resuming later
    if (retryCount === 0) {
      this.lastText = text;
      this.lastOptions = {...options};
      this.lastPosition = startPosition;
    }
    
    // Cancel any existing speech
    if (this.isPlaying) {
      this.stop();
    }
    this.currentText = text;
    
    try {
      // Verify speech synthesis is available
      if (!this.synth || !window.speechSynthesis) {
        logger.error('AudioService', 'Speech synthesis not available at speak time');
        return false;
      }
      
      // Reload voices if needed
      if (this.voices.length === 0) {
        this.voices = this.synth.getVoices();
      }
      
      // For longer texts, break into smaller chunks which are more reliable
      if (text.length > 250 && retryCount === 0) {
        const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
        logger.audio(`Breaking text (${text.length} chars) into ${sentences.length} chunks`);
        
        // Set up data for tracking position across chunks
        this.sentences = sentences;
        this.currentChunkIndex = 0;
        this._pausedAtChunkIndex = 0; // Reset pause tracking for new text
        
        // Determine which chunk to start from based on position
        if (startPosition > 0) {
          let accumulatedLength = 0;
          for (let i = 0; i < sentences.length; i++) {
            const prevLength = accumulatedLength;
            accumulatedLength += sentences[i].length;
            
            if (accumulatedLength >= startPosition) {
              // Found the chunk containing our position
              this.currentChunkIndex = i;
              
              // If we're in the middle of a chunk, we'll need the exact character offset
              // But Web Speech API can't start mid-sentence, so we'll use the full chunk
              logger.debug('AudioService', `Resuming from chunk ${i} based on position ${startPosition}`);
              break;
            }
          }
        }
        
        // Function to speak chunks in sequence
        const speakNextChunk = () => {
          if (this.currentChunkIndex < sentences.length) {
            const chunk = sentences[this.currentChunkIndex];
            logger.debug('AudioService', `Speaking chunk ${this.currentChunkIndex + 1}/${sentences.length}`);
            
            // Calculate exact position at the START of this chunk for resuming
            this.lastPosition = sentences.slice(0, this.currentChunkIndex).reduce((sum, s) => sum + s.length, 0);
            // We'll log the speaking details in the onstart handler instead
            
            // Track the index before incrementing for use in callback
            const chunkIndexBeforeSpeaking = this.currentChunkIndex;
            this.currentChunkIndex++;
            
            // Create options with chain callback for next chunk
            const chunkOptions = {
              ...options,
              onEnd: () => {
                // Only continue with next chunk if we haven't intentionally stopped
                if (!this.intentionalStop && this.currentChunkIndex < sentences.length) {
                  // Small pause between sentences then continue to next chunk
                  setTimeout(speakNextChunk, 100);
                } else if (options.onEnd) {
                  // No more chunks, call the original onEnd callback
                  options.onEnd();
                }
              },
              onError: (e) => {
                // If interrupted by pause, we want to remember which chunk we were on
                if (e.error === 'interrupted' && this.isPaused) {
                  // Go back to the chunk we were speaking when interrupted
                  // CRITICAL: Reset to the current chunk before it was incremented
                  // Revert to the chunk we were actually speaking when interrupted
                  this.currentChunkIndex = chunkIndexBeforeSpeaking;
                  logger.audio(`Pause interrupted chunk ${this.currentChunkIndex + 1}/${sentences.length}`);
                  // Store the actual index for resuming later
                  this._pausedAtChunkIndex = chunkIndexBeforeSpeaking;
                  logger.audio(`Saving _pausedAtChunkIndex = ${this._pausedAtChunkIndex}`);
                } 
                
                if (options.onError) {
                  options.onError(e);
                }
              }
            };
            
            this.speak(chunk, chunkOptions, retryCount + 1, 0); // No need for position within chunk
          }
        };
        
        speakNextChunk();
        return true;
      }
      
      // Create utterance
      this.utterance = new SpeechSynthesisUtterance(text);
      
      // CRITICAL: Add the utterance to the global array to prevent garbage collection in Chrome
      // This is the actual fix for Chrome not firing onend events
      // Ensure the global array exists before pushing to it
      if (!window.utterances) {
        window.utterances = [];
      }
      window.utterances.push(this.utterance);
      
      // Configure utterance (BEFORE setting voice in Chrome)
      this.utterance.rate = options.rate || 0.9; // Slower for children's stories
      this.utterance.pitch = options.pitch || 1.05; // Slight adjustment for clarity
      this.utterance.volume = options.volume || 1.0;
      
      // Select optimal voice
      const selectedVoice = this.selectVoice(options.voiceName);
      
      // Set the voice
      if (selectedVoice) {
        this.utterance.voice = selectedVoice;
      } else if (this.voices.length > 0) {
        this.utterance.voice = this.voices[0]; // Fallback to first available voice
      }
      
      // Add event handlers
      this.utterance.onstart = () => {
        // Comprehensive log with chunk info if available
        if (this.sentences && this.sentences.length > 1) {
          logger.audio(`Speaking chunk ${this.currentChunkIndex}/${this.sentences.length} with voice ${this.utterance.voice?.name || 'default'}`);
        } else {
          logger.audio(`Speaking with voice: ${this.utterance.voice?.name || 'default'}`);
        }
        logger.audio('Speech STARTED event fired');
        this.isPlaying = true;
      };
      
      this.utterance.onend = () => {
        logger.audio('Speech ENDED event fired');
        this.isPlaying = false;
        
        // Prevent memory leak by limiting array size - only keep last 20 utterances
        if (window.utterances.length > 20) {
          logger.audio('Cleaning up old utterances to prevent memory leak');
          window.utterances = window.utterances.slice(-20);
        }
        
        if (options.onEnd) options.onEnd();
      };
      
      // We no longer need the boundary event handler for debugging
      // Now that onEnd is working correctly with the global utterances array
      
      this.utterance.onerror = (e) => {
        // Always log the error for debugging
        if (e.error === 'interrupted') {
          logger.audio('Speech synthesis interrupted (likely user pause/navigation)');
        } else {
          logger.error('AudioService', 'Speech synthesis error', {
          error: e.error,
          message: e.message,
          utteranceText: this.utterance.text.substring(0, 50) + (this.utterance.text.length > 50 ? '...' : ''),
          intentionalStop: this.intentionalStop,
          isPaused: this.isPaused
          });
        }
        
        // Handle intentional stops silently
        if (this.intentionalStop) {
          // Just reset playing state, don't log anything
          this.isPlaying = false;
          return; // Exit early - no error processing needed for intentional stops
        }
        
        // Handle pause-related errors silently - these are expected
        // Add isPaused to the condition since pauseInProgress might not be set in some cases
        if ((this.pauseInProgress || this.isPaused) && e.error === 'interrupted') {
          // Pausing interrupts speech - this is expected behavior, not an error
          logger.debug('AudioService', 'Pause interrupted speech (expected behavior)');
          this.isPlaying = false;
          return; // Exit early - this is part of our pause implementation
        }
        
        // Only log non-intentional stops
        // Specifically check for "interrupted" errors without logging them as errors
        if (e.error === 'interrupted') {
          logger.debug('AudioService', 'Speech was interrupted (possibly by pause/stop)');
          this.isPlaying = false;
        } else {
          logger.error('AudioService', `Speech error: ${e.error}`);
          this.isPlaying = false;
        }
        
        // Only retry if:
        // 1. This wasn't an intentional stop (already checked above)
        // 2. This wasn't a pause operation (already checked above)
        // 3. The error is retryable (interrupted or network)
        // 4. We haven't exceeded retry count
        if (!this.isPaused && (e.error === 'interrupted' || e.error === 'network') && 
            retryCount < MAX_RETRIES) {
          setTimeout(() => {
            this.speak(text, options, retryCount + 1, startPosition);
          }, 500);
          return;
        }
        
        // Call onError callback for non-intentional errors
        if (options.onError) {
          options.onError(e);
        }
      };
      
      // Start speaking (using setTimeout to help with browser restrictions)
      setTimeout(() => {
        if (this.synth && this.synth.paused) this.synth.resume();
        
        if (this.synth && this.utterance) {
          // Log just the voice name in debug mode
          if (process.env.NODE_ENV === 'development') {
            logger.debug('AudioService', `Speaking with voice: ${this.utterance.voice?.name || 'default'}`);
          }
          this.synth.speak(this.utterance);
        } else {
          logger.error('AudioService', 'Failed to speak: synth or utterance not available');
        }
      }, 0);
      
      return true;
    } catch (err) {
      logger.error('AudioService', 'Error in speak method', err);
      return false;
    }
  }

  stop() {
    if (!this.synth) return;
    
    logger.audio('Stopping speech');
    
    // If this is actually a pause operation, we want to remember position
    if (this.shouldRememberPosition) {
      logger.debug('AudioService', 'This is a pause operation, remembering position');
      // Don't set intentionalStop flag, so we can resume later
      this.shouldRememberPosition = false;
    } else {
      // This is a real stop, set the flag to prevent retries
      this.intentionalStop = true;
      // Clear pause state since we're fully stopping
      this.isPaused = false;
    }
    
    // Cancel any speaking utterance
    if (this.synth.speaking) {
      this.synth.cancel();
    }
    
    // Reset state
    this.isPlaying = false;
    
    // If this was a real stop (not a pause), clear the intentional stop flag after a delay
    if (this.intentionalStop) {
      setTimeout(() => {
        this.intentionalStop = false;
      }, 500);
    }
  }

  pause() {
    if (!this.synth) return false;
    
    logger.audio('Pausing speech');
    
    // If we're speaking, we need to save our position
    if (this.synth.speaking) {
      // First make sure we're in paused state
      this.isPaused = true;
      this.isPlaying = false;
      
      // Make sure we keep the position for resuming
      this.shouldRememberPosition = true;
      
      // Log the current chunk for debugging
      if (this.sentences && this.sentences.length > 0 && this.currentChunkIndex < this.sentences.length) {
        // If we're at the next chunk index already (which happens after a chunk finishes),
        // adjust back to the current chunk that's actually speaking
        const activeChunkIndex = Math.max(0, this.currentChunkIndex - 1);
        
        logger.debug('AudioService', `Pausing at chunk ${activeChunkIndex + 1}/${this.sentences.length} (currentChunkIndex: ${this.currentChunkIndex})`);
        
        // IMPORTANT: Use activeChunkIndex which points to the current chunk being spoken
        // This is a fallback in case the onError handler doesn't capture the exact position
        this._pausedAtChunkIndex = activeChunkIndex;
        logger.debug('AudioService', `Setting _pausedAtChunkIndex = ${this._pausedAtChunkIndex}`);
      }
      
      try {
        // Remove any ongoing speech
        this.synth.cancel();
        
        // Immediately notify the UI about pause state
        document.dispatchEvent(new CustomEvent('audioServicePaused', {
          detail: { success: true }
        }));
        
        return true;
      } catch (err) {
        logger.error('AudioService', 'Error during pause', err);
        // Even if there's an error, we're still paused
        document.dispatchEvent(new CustomEvent('audioServicePaused', {
          detail: { success: true }
        }));
        return true;
      }
    }
    
    return false;
  }

  resume() {
    if (!this.synth) return false;
    
    logger.audio('Resuming speech');
    
    // Clear the paused state immediately
    this.isPaused = false;
    
    // If we have a saved position and text, restart from there
    if (this.lastText && this.sentences && this.sentences.length > 0) {
      // Use the specifically saved pause index, which should be the chunk we were interrupted at
      // This is set by both pause() and the onError handler for maximum reliability
      const pausedIndex = Math.min(
        this._pausedAtChunkIndex !== undefined ? this._pausedAtChunkIndex : this.currentChunkIndex, 
        this.sentences.length - 1
      );
      
      logger.debug('AudioService', `Resuming from chunk ${pausedIndex + 1}/${this.sentences.length} (using _pausedAtChunkIndex: ${this._pausedAtChunkIndex})`);
      
      // First set our state to playing - this is important for tests!
      this.isPlaying = true;
      
      // IMPROVED APPROACH: Instead of merging all chunks into one, we'll use
      // the original chunking approach to maintain sentence boundaries
      
      // Reset the current chunk index to the paused chunk
      this.currentChunkIndex = pausedIndex;
      
      // Create a function to speak the remaining chunks sequentially
      // This is similar to the original speakNextChunk function in speak()
      const speakRemainingChunks = () => {
        if (this.currentChunkIndex < this.sentences.length) {
          const chunk = this.sentences[this.currentChunkIndex];
          
          // Calculate exact position at the START of this chunk for resuming
          this.lastPosition = this.sentences.slice(0, this.currentChunkIndex).reduce((sum, s) => sum + s.length, 0);
          
          // Track the index before incrementing for use in callback
          const chunkIndexBeforeSpeaking = this.currentChunkIndex;
          this.currentChunkIndex++;
          
          // Create options with chain callback for next chunk
          const chunkOptions = {
            ...this.lastOptions,
            onEnd: () => {
              // Only continue with next chunk if we haven't intentionally stopped
              if (!this.intentionalStop && this.currentChunkIndex < this.sentences.length) {
                setTimeout(speakRemainingChunks, 100); // Small pause between sentences
              } else if (this.lastOptions.onEnd) {
                this.lastOptions.onEnd();
              }
            },
            onError: (e) => {
              // If interrupted by pause, remember which chunk we were on
              if (e.error === 'interrupted' && this.isPaused) {
                // Revert to the chunk we were actually speaking when interrupted
                this.currentChunkIndex = chunkIndexBeforeSpeaking;
                logger.audio(`Pause interrupted chunk ${this.currentChunkIndex + 1}/${this.sentences.length}`);
                // Store the actual index for resuming later
                this._pausedAtChunkIndex = chunkIndexBeforeSpeaking;
                logger.audio(`Saving _pausedAtChunkIndex = ${this._pausedAtChunkIndex}`);
              } 
              
              if (this.lastOptions.onError) {
                this.lastOptions.onError(e);
              }
            }
          };
          
          // Speak just this one chunk, not merging everything
          this.speak(chunk, chunkOptions, 1, 0); // Retries start at 1 to avoid re-chunking
        }
      };
      
      // Start speaking from the paused chunk
      speakRemainingChunks();
      return true;
    } else if (this.lastText) {
      // Fallback approach: restart from beginning of text
      logger.audio('Resuming from beginning of text (no chunk info available)');
      return this.speak(this.lastText, this.lastOptions);
    }
    
    return false;
  }

  getState() {
    if (!this.synth) {
      return { isPlaying: false, isPaused: false, isSpeaking: false };
    }
    
    return {
      isPlaying: this.isPlaying,
      isPaused: this.synth.paused,
      isSpeaking: this.synth.speaking
    };
  }

  selectVoice(preferredVoice) {
    // Check if we have any voices at all
    if (this.voices.length === 0) {
      logger.audio('No voices available for selection', true);
      return null;
    }
    
    // If user explicitly requested a voice by name and it exists, use it
    if (preferredVoice && this.voices.find(v => v.name === preferredVoice)) {
      const requestedVoice = this.voices.find(v => v.name === preferredVoice);
      logger.audio(`Using requested voice: ${requestedVoice.name}`);
      return requestedVoice;
    }
    
    // Priority 1: Google UK English Female (top priority)
    const googleUkFemale = this.voices.find(v => 
      v.name === 'Google UK English Female'
    );
    
    // Priority 2: Other Google natural-sounding voices (premium quality in Chrome)
    const googleVoices = this.voices.filter(v => 
      v.name.includes('Google') && 
      v.lang.startsWith('en') && 
      !v.name.includes('Standard') && // Avoid Standard Google voices which are more robotic
      v.name !== 'Google UK English Female' // Exclude UK Female as it's handled separately
    );
    
    // Priority 3: Microsoft premium voices (often available in Edge)
    const microsoftPremiumVoices = this.voices.filter(v => 
      v.name.includes('Microsoft') && 
      v.lang.startsWith('en') && 
      (v.name.includes('Neural') || v.name.includes('Premium'))
    );
    
    // Priority 4: Any English Google voice
    const anyGoogleEnglishVoices = this.voices.filter(v => 
      v.name.includes('Google') && 
      v.lang.startsWith('en') &&
      v.name !== 'Google UK English Female' // Exclude UK Female as it's handled separately
    );
    
    // Priority 5: Any English female voice
    const englishFemaleVoices = this.voices.filter(v => 
      v.lang.startsWith('en') && 
      (v.name.toLowerCase().includes('female') || 
       v.name.includes('Samantha') ||
       v.name.includes('Victoria') ||
       v.name.includes('Karen') ||
       v.name.includes('Moira')) &&
      v.name !== 'Google UK English Female' // Exclude UK Female as it's handled separately
    );
    
    // Priority 6: Any English voice
    const englishVoices = this.voices.filter(v => v.lang.startsWith('en'));
    
    // Combine and prioritize the voice lists
    const prioritizedVoices = [
      ...(googleUkFemale ? [googleUkFemale] : []), // Add UK Female at the top if available
      ...googleVoices,
      ...microsoftPremiumVoices,
      ...anyGoogleEnglishVoices,
      ...englishFemaleVoices,
      ...englishVoices,
      ...this.voices  // All voices as final fallback
    ];
    
    // Remove duplicates while preserving order
    const uniqueVoices = [...new Map(prioritizedVoices.map(v => [v.name, v])).values()];
    
    // Choose the highest priority voice (first in the list)
    if (uniqueVoices.length > 0) {
      const selectedVoice = uniqueVoices[0];
      // Voice selection is now logged in the onstart handler
      return selectedVoice;
    }
    
    // Absolute fallback to first voice
    logger.debug('AudioService', "No suitable voices found, using default voice");
    return this.voices[0];
  }

  // Check if Web Speech API is supported
  static isSupported() {
    // Check if speechSynthesis is available
    const isSpeechAvailable = 'speechSynthesis' in window && 
                             'SpeechSynthesisUtterance' in window;
    
    if (!isSpeechAvailable) {
      logger.audio('Web Speech API not available in this browser', true);
      return false;
    }
    
    // Check browser type
    const userAgent = navigator.userAgent.toLowerCase();
    const isChrome = /chrome/.test(userAgent) && !/chromium/.test(userAgent);
    const isEdge = /edg(e|)\//.test(userAgent);
    const isOpera = /opera/.test(userAgent) || /opr/.test(userAgent);
    const isChromium = /chromium/.test(userAgent);
    
    // Chrome, Edge, and Opera provide the best text-to-speech
    const isPremiumVoiceBrowser = isChrome || isEdge || isOpera || isChromium;
    
    if (isPremiumVoiceBrowser) {
      logger.audio('Premium voice browser detected (Chrome/Edge/Opera)');
      return true;
    } else if (isSpeechAvailable) {
      logger.audio('Basic speech synthesis support detected');
      return true;
    }
    
    return false;
  }
  
  // Get browser information for voice compatibility
  static getBrowserInfo() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/chrome/.test(userAgent) && !/chromium/.test(userAgent)) return 'Chrome';
    if (/edg/.test(userAgent)) return 'Edge';
    if (/opera/.test(userAgent) || /opr/.test(userAgent)) return 'Opera';
    if (/firefox/.test(userAgent)) return 'Firefox';
    if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) return 'Safari';
    
    return 'Unknown';
  }
}

export default AudioService;